import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import {
  BookmarkIcon,
  CalendarIcon,
  CheckIcon,
  CircleArrowRightIcon,
  HashIcon,
  LayoutGridIcon,
  MessagesSquareIcon,
  NetworkIcon,
  PinIcon,
  ThumbsUpIcon,
} from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import {
  LabelChooser,
  LabelStackView,
} from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { Button } from "simplydo/react/ui/button";
import {
  Card,
  CardContent,
  CardAction,
  CardImage,
} from "simplydo/react/ui/card";
import { H3, H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import { Popup } from "simplydo/react/ui/popup";
import { Progress } from "simplydo/react/ui/progress";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { IdeaBusinessProfileChip, UserChip } from "components/lib/Chips";
import TagModalChooser, {
  TagView,
} from "components/lib/Choosers/Tags/TagModalChooser";
import { IdeaLikeHandler } from "components/lib/Ideas";
import PinIdeaHelper from "components/lib/Ideas/PinIdeaHelper";

import { IdeaAssessmentProgress } from "../IdeaAssessmentProgress";
import IdeaDisplayFields from "../IdeaDisplayFields";
import { type IdeaCardViewProps, type IdeaDisplayField } from "../types";
import {
  addTag,
  labelIdea,
  pinIdea,
  removeTag,
  voteForIdea,
} from "./ideaViewActions";

const IdeaCardView = ({
  idea,
  challenge,
  onSelect,
  selectedIdeas,
  ideasVotedFor,
  setPreviewOpen,
}: IdeaCardViewProps) => {
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();

  const [tagsOpen, setTagsOpen] = useState(false);

  const projectManagement = idea.projectManagement;
  const pmBoard = projectManagement?.boards?.filter(
    (b: NonNullable<typeof projectManagement>["boards"][number]) =>
      b.forId === challenge?._id,
  )[0];
  const pmBoardEnabled = pmBoard?.isEnabled;

  const isSelected = selectedIdeas?.includes(idea._id ?? "");
  const completenessPercentage = Math.round((idea.completeness || 0) * 100);
  const ideaComplete = idea.completeness === 1;
  const usedVotes =
    parseInt(String(challenge.voteLimit ?? 0), 10) === ideasVotedFor;
  const showProgress = !!completenessPercentage && !idea.isSubmitted;

  const hasAssessments = useMemo(
    () =>
      (idea.assessments?.length ?? 0) > 0 ||
      (idea.invitedExternalAssessors?.length ?? 0) > 0,
    [idea],
  );

  const invitationQuery = useQuery(
    initUseQuery(api.invitations.getForType, ["ideaAssessor", idea.challenge]),
  );

  const pendingInvitations = invitationQuery.data?.invitations.filter((i) =>
    i?.forIdeas?.includes(idea._id),
  );
  const navigate = useNavigate();

  const openIdea = (options: { toComments?: boolean } = {}) => {
    if (setPreviewOpen) {
      setPreviewOpen(idea._id, { toComments: options?.toComments });
    } else {
      navigate(`/ideas/${idea._id}${options?.toComments ? "#comments" : ""}`);
    }
  };

  const { activityInfo = {}, createdAt = 0, updatedAt } = idea;

  const { lastActivityAt = updatedAt ?? createdAt } = activityInfo;

  const displayFields: IdeaDisplayField[] = (
    challenge?.ownerIdeaTemplate?.body
      ?.filter((section) => section.type !== "assessment")
      .flatMap((section) =>
        section.fields.filter(
          (field) => field.isDisplayField && idea.templated?.[field.id],
        ),
      ) || []
  ).map((field) => ({
    id: field.id,
    value: idea.templated?.[field.id],
    title: field.title,
    type: field.type,
  }));

  const statusLabelStyle = {
    position: "absolute" as const,
    bottom: 5,
    right: 5,
  };

  return (
    <Card
      fluid
      elevation="large"
      onClick={() => {
        openIdea();
      }}
      style={{ height: "100%" }}
    >
      <CardImage dimmed>
        <ImageWithFallback
          src={util.ideaCoverImage(idea, "640x100")}
          fallbackSrc={util.ideaCoverImage(idea)}
        />
        {showProgress ? (
          <Popup
            trigger={
              <Progress
                size="large"
                style={{ zIndex: 1 }}
                label={`${completenessPercentage}% complete`}
                indicating={!ideaComplete}
                color={ideaComplete ? "green" : "yellow"}
                percent={completenessPercentage}
                attached="top"
              />
            }
          >{`${t("common:capitalise", { key: "generic.idea" })} is ${completenessPercentage}% complete`}</Popup>
        ) : null}

        {idea.isSubmitted ? (
          <Label
            style={{ marginTop: 10 }}
            size="small"
            color="green"
            ribbon="right"
          >
            {t("ideas.card.submitted")}
          </Label>
        ) : (
          <Label
            style={{ marginTop: 10 }}
            size="small"
            color="yellow"
            ribbon="right"
          >
            {t("ideas.card.draft")}
          </Label>
        )}
        {util.canManageChallenge(user, challenge) ||
        util.hasPermission(
          user,
          "challenge.editIdeaDeadlines",
          challenge?._id,
        ) ||
        util.hasPermission(user, "challenge.viewIdeas", challenge?._id) ? (
          <div style={statusLabelStyle}>
            {idea.submissionDeadline ? (
              <Popup
                trigger={
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    color="secondary"
                  >
                    <Icon icon={CalendarIcon} />
                  </Button>
                }
              >{`This ${t("generic.idea")} has a custom deadline: ${formatDate(idea.submissionDeadline.deadline)}`}</Popup>
            ) : null}
            <LabelChooser
              trigger={
                <Button
                  size="small"
                  color={
                    (idea.labels || []).length > 0 ? "secondary" : undefined
                  }
                >
                  <Icon icon={BookmarkIcon} />
                </Button>
              }
              tooltip="Add or remove labels"
              onComplete={(em) => labelIdea(idea, em, true)}
              existing={idea.labels}
              onRemoveExisting={(em) => labelIdea(idea, em, false)}
            />
            <Popup
              trigger={
                <Button
                  size="small"
                  onClick={(e) => {
                    pinIdea(idea, !idea.isPinned);
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  color={idea.isPinned ? "secondary" : undefined}
                >
                  <Icon icon={PinIcon} />
                </Button>
              }
            >
              {
                <div>
                  <p>
                    <b>
                      {idea.isPinned
                        ? t("ideas.options.pin.unPin")
                        : t("ideas.options.pin.challenge")}
                    </b>
                  </p>

                  <PinIdeaHelper challenge={challenge} />
                </div>
              }
            </Popup>
            {onSelect ? (
              <Button
                size="small"
                onClick={(e) => {
                  onSelect(idea);
                  e.preventDefault();
                  e.stopPropagation();
                }}
                color={isSelected ? "secondary" : undefined}
              >
                <Icon icon={CheckIcon} />
              </Button>
            ) : null}
          </div>
        ) : (
          <div style={statusLabelStyle}>
            {idea.isPinned ? (
              <Button size="small" color="secondary">
                <Icon icon={PinIcon} />
              </Button>
            ) : null}
            {onSelect ? (
              <Button
                size="small"
                onClick={(e) => {
                  onSelect(idea);
                  e.preventDefault();
                  e.stopPropagation();
                }}
                color={isSelected ? "secondary" : undefined}
              >
                <Icon icon={CheckIcon} />
              </Button>
            ) : null}
          </div>
        )}
      </CardImage>
      <CardContent>
        <H3 style={{ color: "#4d4949" }}>
          {idea.children ? <Icon icon={NetworkIcon} size="small" /> : null}
          {idea.name}
        </H3>
        {idea.ownerChallenge && (
          <div style={{ marginBottom: 5 }}>
            <Link
              style={{ textDecorationLine: "underline" }}
              to={`/challenges/${idea.ownerChallenge._id}`}
            >
              {idea.ownerChallenge?.name}
            </Link>
          </div>
        )}

        {idea?.owner ? (
          <div>
            <span
              style={{
                display: "flex",
                color: "gray",
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
            >
              {idea.lockedOwnerIdeaBusinessProfile ? (
                <IdeaBusinessProfileChip
                  ideaBusinessProfile={idea.lockedOwnerIdeaBusinessProfile}
                  compact="very"
                />
              ) : (
                <UserChip user={idea.owner} compact="very" />
              )}
              {(idea.collaborators?.length ||
                !!idea.lockedOwnerIdeaBusinessProfile?.name) &&
              idea.authors ? (
                <span
                  onClick={(e) => e.stopPropagation()}
                  style={{ marginLeft: 4 }}
                >
                  <Popup
                    on="click"
                    trigger={
                      <span
                        style={{
                          cursor: "pointer",
                          userSelect: "none",
                          textDecoration: "underline",
                        }}
                        onClick={(e) => e.preventDefault()}
                      >
                        +{" "}
                        {util.pluralise(
                          idea.lockedOwnerIdeaBusinessProfile?.name
                            ? (idea.collaborators?.length || 0) + 1
                            : (idea.collaborators?.length ?? 0),
                          "other",
                          "others",
                        )}
                      </span>
                    }
                  >
                    {
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 5,
                        }}
                      >
                        {idea.authors
                          ?.filter(
                            (a) =>
                              !!idea.lockedOwnerIdeaBusinessProfile?.name ||
                              a._id !== idea.user,
                          )
                          .map((a) => (
                            <UserChip key={a._id} user={a} />
                          ))}
                      </div>
                    }
                  </Popup>
                </span>
              ) : null}
            </span>
            <p style={{ marginTop: 4, fontSize: 13, color: "grey" }}>
              {idea.isSubmitted
                ? `${t("ideas.card.submittedOn", { date: formatDate(idea.submittedAt, "dd/MM/yy") })}`
                : `${t("ideas.card.updatedAt", { date: formatDate(idea.updatedAt, "dd/MM/yy") })}`}
              , last activity on {formatDate(lastActivityAt, "dd/MM/yy")}.
            </p>
          </div>
        ) : null}

        {(util.canManageChallenge(user, challenge) ||
          util.hasPermission(
            user,
            "challenge.viewProjectBoard",
            challenge?._id,
          )) &&
          pmBoardEnabled &&
          pmBoard?.laneObject && (
            <div style={{ marginTop: 5, display: "inline-flex" }}>
              <Icon style={{ color: "#aab6b2" }} icon={LayoutGridIcon} />
              <p style={{ color: "#aab6b2" }}>{pmBoard.laneObject?.name}</p>
            </div>
          )}

        <div
          style={{
            display: "flex",
            maxWidth: "100%",
            flexWrap: "wrap",
            marginTop: 5,
          }}
        >
          {(util.canManageChallenge(user, challenge) ||
            util.hasPermission(
              user,
              "challenge.viewAssessments",
              challenge?._id,
            )) &&
          hasAssessments ? (
            <IdeaAssessmentProgress idea={idea} challenge={challenge} compact />
          ) : null}
          {idea.labels ? (
            <LabelStackView labels={idea.labels} horizontal />
          ) : null}
        </div>
        {displayFields.length > 0 ? (
          <div style={{ marginTop: 8 }}>
            <IdeaDisplayFields fields={displayFields} />
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-start",
            width: "100%",
            marginTop: 10,
          }}
        >
          <TagModalChooser
            hideView={true}
            isOpen={tagsOpen}
            onClose={() => setTagsOpen(false)}
            tags={idea.ownerTags ?? []}
            availableTags={
              challenge.tagList?.filter(
                (tag) => !tag?.disabledEntities?.includes("ideas"),
              ) ?? []
            }
            addTag={(tag) => addTag(idea, tag)}
            removeTag={(tag) => removeTag(idea, tag)}
            canEdit={
              util.canManageChallenge(user, challenge) ||
              util.hasPermission(user, "challenge.editIdeaTags", challenge?._id)
            }
          />

          {(challenge?.tagList?.length ?? 0) > 0 && (
            <Popup
              on="click"
              trigger={
                <Button
                  size="small"
                  label={
                    <Label basic pointing="left">
                      {idea.tags?.length ?? 0}
                    </Label>
                  }
                >
                  <Icon icon={HashIcon} />
                </Button>
              }
            >
              {
                <div>
                  <H4>Tags</H4>
                  <TagView tags={idea.ownerTags || []} />
                  {util.canManageChallenge(user, challenge) ||
                  util.hasPermission(
                    user,
                    "challenge.editIdeaTags",
                    challenge?._id,
                  ) ? (
                    <Button
                      size="small"
                      style={{ marginLeft: 3 }}
                      onClick={() => {
                        setTagsOpen(true);
                      }}
                    >
                      <Icon icon={HashIcon} />
                      {t("tags.edit")}
                    </Button>
                  ) : null}
                </div>
              }
            </Popup>
          )}
          {!challenge?.preventIdeaComments && (
            <Button
              onClick={() => {
                openIdea({ toComments: true });
              }}
              color={idea.commentCount ? "blue" : "grey"}
              size="small"
              label={
                <Label basic pointing="left">
                  {idea.commentCount || 0}
                </Label>
              }
              style={{ marginLeft: 5 }}
            >
              <Icon icon={MessagesSquareIcon} />
            </Button>
          )}

          {challenge.votingVisibility === "users" ? (
            <IdeaLikeHandler
              idea={idea}
              challenge={challenge}
              onLikeIdea={(ideaId, addVote) => voteForIdea(idea, addVote)}
              trigger={
                <Button
                  disabled={usedVotes && !idea.votedFor}
                  color={idea.votedFor ? "green" : "grey"}
                  style={{ marginLeft: 5 }}
                  size="small"
                  label={
                    util.hasPermission(
                      user,
                      "challenge.viewIdeas",
                      challenge?._id,
                    ) ||
                    util.canManageChallenge(user, challenge) ||
                    challenge.voteCountVisibility === "users" ? (
                      <Label basic pointing="left">
                        {idea.voteCount || 0}
                      </Label>
                    ) : null
                  }
                >
                  <Icon icon={ThumbsUpIcon} />
                </Button>
              }
            />
          ) : null}
        </div>
      </CardContent>
      <CardAction color="primary" onClick={() => openIdea()}>
        {t("ideas.card.view")} <Icon icon={CircleArrowRightIcon} />
      </CardAction>
    </Card>
  );
};

export default IdeaCardView;
